/**
 * PillarNav — Primary pillar tabs (Cultivate | Connect | Command | Chronicle),
 * plus a fifth Admin tab for super_admin users.
 *
 * Rendered inline inside the top navbar row between brand and right controls.
 */
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PILLAR_CONFIG, ADMIN_PILLAR, getActivePillar, type PillarId } from './navConfig';

interface PillarNavProps {
  /** When set, highlight this pillar even if the URL doesn't match. */
  overridePillarId?: PillarId | null;
}

const PillarNav: React.FC<PillarNavProps> = ({ overridePillarId }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const activePillar = overridePillarId || getActivePillar(pathname);
  const pillars = user?.role === 'super_admin' ? [...PILLAR_CONFIG, ADMIN_PILLAR] : PILLAR_CONFIG;

  return (
    <div className="hidden md:flex items-center gap-0.5 ml-4" role="navigation" aria-label="Pillar navigation">
      {pillars.map((pillar) => {
        const isActive = activePillar === pillar.id;

        return (
          <NavLink
            key={pillar.id}
            to={pillar.baseRoute}
            className={`px-3.5 py-1.5 font-semibold text-sm no-underline rounded-lg transition-colors whitespace-nowrap ${
              isActive
                ? 'text-[var(--pillar-accent,var(--theme-primary))] bg-white/[0.06]'
                : 'text-text-secondary hover:text-text-primary hover:bg-[var(--theme-bg-tertiary)]'
            }`}
            style={isActive ? { '--pillar-accent': `var(${pillar.accentVar})` } as React.CSSProperties : undefined}
            aria-current={isActive ? 'page' : undefined}
          >
            {pillar.label}
          </NavLink>
        );
      })}
    </div>
  );
};

export default PillarNav;
